import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapPin, X } from 'lucide-react';
import {
  WORLD_CITY_REGIONS,
  findWorldCity,
  gmtOffsetBadge,
  searchWorldCities,
  type WorldCity,
} from './worldCities';

type Props = {
  /** Ville sélectionnée (texte libre autorisé). */
  value: string;
  /** Appelé avec la ville et, si connue, son fuseau IANA à présélectionner. */
  onChange: (city: string, timezone?: string) => void;
  label?: string;
  placeholder?: string;
  compact?: boolean;
};

export const CityPicker: React.FC<Props> = ({
  value,
  onChange,
  label = 'Ville / Lieu',
  placeholder = 'Rechercher une ville…',
  compact = false,
}) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const results = useMemo(() => searchWorldCities(query, 40), [query]);

  const grouped = useMemo(() => {
    const map = new Map<string, WorldCity[]>();
    results.forEach((city) => {
      const list = map.get(city.region) ?? [];
      list.push(city);
      map.set(city.region, list);
    });
    return WORLD_CITY_REGIONS.filter((region) => map.has(region)).map((region) => ({
      region,
      cities: map.get(region)!,
    }));
  }, [results]);

  const knownCity = findWorldCity(value);
  const trimmedQuery = query.trim();
  const exactMatch = findWorldCity(trimmedQuery);

  const selectCity = (city: WorldCity) => {
    onChange(city.name, city.timezone);
    setQuery('');
    setOpen(false);
  };

  const applyFreeText = () => {
    if (!trimmedQuery) return;
    onChange(trimmedQuery);
    setQuery('');
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label className="mb-2 block text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</label>
      )}

      {value && !open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={[
            'flex w-full items-center gap-2.5 rounded-xl border-2 border-slate-200 bg-slate-50 text-left transition-colors hover:border-indigo-300 hover:bg-white',
            compact ? 'px-3 py-2.5' : 'px-4 py-3.5',
          ].join(' ')}
        >
          <MapPin className="h-4 w-4 shrink-0 text-indigo-500" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-bold text-slate-900">{value}</span>
            {knownCity && (
              <span className="block truncate text-xs text-slate-500">
                {knownCity.country} · GMT{gmtOffsetBadge(knownCity.timezone)}
              </span>
            )}
          </span>
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onChange('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                onChange('');
              }
            }}
            className="rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700"
            aria-label="Effacer la ville"
          >
            <X className="h-3.5 w-3.5" />
          </span>
        </button>
      ) : (
        <div className="relative">
          <MapPin className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (exactMatch) selectCity(exactMatch);
                else if (results.length === 1) selectCity(results[0]);
                else applyFreeText();
              }
              if (e.key === 'Escape') setOpen(false);
            }}
            placeholder={placeholder}
            className={[
              'w-full rounded-xl border-2 border-slate-200 bg-white pl-10 pr-4 text-sm font-semibold text-slate-900 transition-colors focus:border-indigo-500 focus:outline-none focus:ring-4 focus:ring-indigo-500/10',
              compact ? 'py-2.5' : 'py-3.5',
            ].join(' ')}
          />
        </div>
      )}

      {open && (
        <div className="absolute left-0 right-0 z-30 mt-1.5 max-h-72 overflow-y-auto rounded-2xl border border-slate-200 bg-white py-1.5 shadow-xl shadow-slate-900/10">
          {grouped.length === 0 && !trimmedQuery && (
            <p className="px-4 py-3 text-sm text-slate-500">Aucune ville.</p>
          )}
          {grouped.map(({ region, cities }) => (
            <div key={region}>
              <p className="px-4 pb-1 pt-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">{region}</p>
              {cities.map((city) => (
                <button
                  key={`${city.name}-${city.country}`}
                  type="button"
                  onClick={() => selectCity(city)}
                  className="flex w-full items-center gap-2.5 px-4 py-2 text-left transition-colors hover:bg-indigo-50"
                >
                  <span className="flex h-7 w-11 shrink-0 items-center justify-center rounded-md bg-indigo-100 text-[10px] font-black text-indigo-700">
                    {gmtOffsetBadge(city.timezone)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-bold text-slate-900">{city.name}</span>
                    <span className="block truncate text-xs text-slate-500">{city.country}</span>
                  </span>
                  <span className="shrink-0 text-[11px] font-semibold text-slate-400">
                    GMT{gmtOffsetBadge(city.timezone)}
                  </span>
                </button>
              ))}
            </div>
          ))}
          {trimmedQuery && !exactMatch && (
            <button
              type="button"
              onClick={applyFreeText}
              className="mt-1 flex w-full items-center gap-2 border-t border-slate-100 px-4 py-2.5 text-left text-sm font-semibold text-indigo-600 transition-colors hover:bg-indigo-50"
            >
              <MapPin className="h-4 w-4" />
              Utiliser « {trimmedQuery} » (lieu personnalisé)
            </button>
          )}
        </div>
      )}
    </div>
  );
};
